import React, { useState, useEffect } from 'react';
import { placementDriveService, companyService, countryService, stateService, districtService } from '../../services/api';
import { getUserData } from '../../utils/auth';
import './CoordinatorPlacementDrives.css';

function CoordinatorPlacementDrives() {
  const userData = getUserData();
  const collegeId = userData?.collegeId || userData?.CollegeId;
  const cordinatorId = userData?.cordinatorId || userData?.CordinatorId;

  const [drives, setDrives] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    Title: '',
    Description: '',
    LastDate: '',
    CompanyId: '',
    CountryId: '',
    StateId: '',
    DistrictId: '',
    CollegeId: collegeId || ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (collegeId) loadData();
  }, [collegeId]);

  useEffect(() => {
    loadDropdowns();
  }, []);

  useEffect(() => {
    if (formData.CountryId) {
      stateService.getStates().then((data) => setStates(data.filter(s => s.CountryId === parseInt(formData.CountryId)))).catch(() => setStates([]));
    } else setStates([]);
  }, [formData.CountryId]);

  useEffect(() => {
    if (formData.StateId) {
      districtService.getDistricts().then((data) => setDistricts(data.filter(d => d.StateId === parseInt(formData.StateId)))).catch(() => setDistricts([]));
    } else setDistricts([]);
  }, [formData.StateId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await placementDriveService.GetPlacementDrivesByCollege(collegeId);
      setDrives(data || []);
    } catch (error) {
      alert('Error loading placement drives: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadDropdowns = async () => {
    try {
      const [companiesData, countriesData] = await Promise.all([
        companyService.getCompanies(),
        countryService.getCountries()
      ]);
      setCompanies(companiesData || []);
      setCountries(countriesData || []);
    } catch (error) {
      console.error('Error loading dropdowns:', error);
    }
  };

  const handleAdd = () => {
    setEditing(null);
    setFormData({ Title: '', Description: '', LastDate: '', CompanyId: '', CountryId: '', StateId: '', DistrictId: '', CollegeId: collegeId });
    setIsModalOpen(true);
  };

  const handleEdit = (drive) => {
    setEditing(drive);
    setFormData({
      Id: drive.Id,
      Title: drive.Title || '',
      Description: drive.Description || '',
      LastDate: drive.LastDate ? drive.LastDate.split('T')[0] : '',
      CompanyId: drive.CompanyId || '',
      CountryId: drive.CountryId || '',
      StateId: drive.StateId || '',
      DistrictId: drive.DistrictId || '',
      CollegeId: drive.CollegeId || collegeId
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const payload = {
        ...formData,
        CompanyId: formData.CompanyId || null,
        CollegeId: collegeId,
        CordinatorId: cordinatorId || null
      };
      await placementDriveService.savePlacementDrive(payload);
      setIsModalOpen(false);
      loadData();
    } catch (error) {
      alert('Error saving: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!collegeId) return <div className="coord-places"><p>College session not found.</p></div>;

  return (
    <div className="coord-places">
      <div className="page-header">
        <h1>Placement Drives (College)</h1>
        <p>Manage placement drives for your college</p>
        <button className="btn-add" onClick={handleAdd}>+ Add Placement Drive</button>
      </div>

      {loading && !drives.length ? <p>Loading...</p> : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr><th>Title</th><th>Company</th><th>Last Date</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {drives.map((d) => (
                <tr key={d.Id}>
                  <td>{d.Title}</td>
                  <td>{d.Company?.Name}</td>
                  <td>{d.LastDate ? new Date(d.LastDate).toLocaleDateString() : ''}</td>
                  <td>
                    <button className="btn-edit" onClick={() => handleEdit(d)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {drives.length === 0 && <p className="no-data">No placement drives yet.</p>}
        </div>
      )}

      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Edit' : 'Add'} Placement Drive</h2>
              <button className="modal-close" onClick={() => setIsModalOpen(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group"><label>Title *</label><input type="text" value={formData.Title} onChange={(e) => setFormData({ ...formData, Title: e.target.value })} required /></div>
              <div className="form-group"><label>Description</label><textarea value={formData.Description} onChange={(e) => setFormData({ ...formData, Description: e.target.value })} rows={3} /></div>
              <div className="form-group"><label>Last Date</label><input type="date" value={formData.LastDate} onChange={(e) => setFormData({ ...formData, LastDate: e.target.value })} /></div>
              <div className="form-group"><label>Company</label>
                <select value={formData.CompanyId} onChange={(e) => setFormData({ ...formData, CompanyId: e.target.value })}>
                  <option value="">Select Company</option>
                  {companies.map((c) => <option key={c.Id} value={c.Id}>{c.Name}</option>)}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Country</label>
                  <select value={formData.CountryId} onChange={(e) => setFormData({ ...formData, CountryId: e.target.value, StateId: '', DistrictId: '' })}>
                    <option value="">Select</option>
                    {countries.map((c) => <option key={c.Id} value={c.Id}>{c.Name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>State</label>
                  <select value={formData.StateId} onChange={(e) => setFormData({ ...formData, StateId: e.target.value, DistrictId: '' })} disabled={!formData.CountryId}>
                    <option value="">Select</option>
                    {states.map((s) => <option key={s.Id} value={s.Id}>{s.Name}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>District</label>
                  <select value={formData.DistrictId} onChange={(e) => setFormData({ ...formData, DistrictId: e.target.value })} disabled={!formData.StateId}>
                    <option value="">Select</option>
                    {districts.map((d) => <option key={d.Id} value={d.Id}>{d.Name}</option>)}
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setIsModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn-save" disabled={loading}>{loading ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CoordinatorPlacementDrives;
